<template>
   <div>
      <div v-if="!loaded">
         <SpinnerOverlay />
      </div>
      <div v-if="loaded">
         <b-container>
            <ExpensesHeader :expense-type="expenseType"></ExpensesHeader>
            <PerPeriodMessage :expense-type="expenseType" :loading="dataValidationLoading" />
         </b-container>
         <b-container v-if="assignedUsers.length > 0">
            <b-card class="fluid-transition" style="width: 100%; border-radius: 25px">
               <PeriodSelector :expense-type="expenseType" />
               <div v-for="(assigneeId, index) in assignedUsers" :key="assigneeId">
                  <CollapseCard
                     :key="assigneeId"
                     :ident="`${index}`"
                     class="account-section"
                     :subtitle="`${
                        accountCounts[assigneeId] === 1
                           ? `1 ${entityLabelSingularLower}`
                           : `${accountCounts[assigneeId]} ${entityLabelPluralLower}`
                     }`"
                  >
                     <template #title>
                        <div class="d-flex align-items-flex-start flex-column">
                           <small>
                              <RdigStatusLabel
                                 :ident="`acc-${periodId}-${assigneeId}`"
                                 class="mr-2 assignment-status"
                                 :status="mergedStatuses[assigneeId]"
                              />
                           </small>
                           <p class="assignment-title mt-2">
                              {{
                                 assigneeId === profile.id
                                    ? 'Assigned to Me'
                                    : userCardTitle(assigneeId)
                              }}
                           </p>
                        </div>
                     </template>
                     <div class="assignment-table-wrapper">
                        <div v-if="dataValidationLoading" class="data-validation-loading-overlay">
                           <p>
                              Data for selected period is being validated. This may take a while.
                              <br />
                              Please wait...
                              <br />
                              <b-spinner variant="primary" class="loading-spinner"></b-spinner>
                           </p>
                        </div>
                        <TimePeriodTable :assigneeId="assigneeId" :expense-type="expenseType" />
                        <div class="assignment-completion">
                           <b-button
                              id="btn-assignment-complete-assignments"
                              class="action-button"
                              variant="success"
                              v-if="assigneeId === profile.id && !isRndig"
                              @click="submit(true)"
                              :disabled="isAssignmentMarkCompletedDisabled"
                              style="font-size: 0.75rem"
                           >
                              Mark Assignment Complete <b-icon-check />
                           </b-button>
                        </div>
                     </div>
                  </CollapseCard>
                  <div
                     v-if="index !== assignedUsers.length - 1"
                     style="width: 100%; border-top: 1px solid #e2e2e2; height: 1px"
                  />
               </div>
            </b-card>
         </b-container>
         <b-container class="d-flex align-items-center justify-content-between mt-4">
            <div>
               <b-button
                  class="d-flex align-items-center dark-button pr-4 pl-2"
                  variant="primary"
                  :to="$clientStaffRoute({name: `${expenseType}-table`})"
                  style="background-color: #1560a8; font-size: 0.75rem"
               >
                  <b-icon-arrow-left-short class="mr-1" />
                  Back
               </b-button>
            </div>
            <div>
               <b-button
                  id="btn-validate-period-assignments"
                  class="action-button mr-2"
                  variant="success"
                  v-if="isRndig"
                  @click="submit(true)"
                  :disabled="isAssignmentMarkCompletedDisabled"
                  style="font-size: 0.75rem"
               >
                  Validate and Complete Period<b-icon-check />
               </b-button>
               <b-button
                  id="btn-complete-assignments"
                  class="action-button"
                  variant="success"
                  v-if="!isSME"
                  @click="submit(false)"
                  :disabled="isMarkCompletedDisabled"
                  style="font-size: 0.75rem"
               >
                  Mark Section Complete<b-icon-check />
               </b-button>
            </div>
         </b-container>
      </div>
   </div>
</template>

<script>
import {mapGetters} from 'vuex';
import TimePeriodTable from './components/TimePeriodTable.vue';
import PeriodSelector from './components/PeriodSelector.vue';
import ExpensesHeader from './components/ExpensesHeader.vue';
import SpinnerOverlay from '@/views/common/widgets/SpinnerOverlay.vue';
import PerPeriodMessage from './components/PerPeriodMessage.vue';
import {EXPENSE_ENTITY_LABEL_CONFIG, resolveAssignmentType} from '@/helpers/baseConfig';
import ErrorCodes from '@/helpers/errorCodes';
import {
   getRelevantFlags,
   hasUnresolvedFlags as hasUnresolvedExpenseFlags,
   isAssignmentMarkCompletedDisabled as isAssignmentMarkCompletedDisabledRule,
   isSectionMarkCompletedDisabled as isSectionMarkCompletedDisabledRule,
} from '@/helpers/expenseCompletion';

export default {
   components: {
      PerPeriodMessage,
      ExpensesHeader,
      SpinnerOverlay,
      TimePeriodTable,
      PeriodSelector,
   },

   props: {
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
   },

   data() {
      return {
         loaded: false,
         dataValidationLoading: false,
         assignmentsLoaded: false,
      };
   },

   computed: {
      ...mapGetters({
         profile: 'profile',
         isRndig: 'isRndig',
         activeStudy: 'companies/activeStudy',
         activeStudyId: 'companies/activeStudyId',
         studyPeriodMap: 'companies/studyPeriodMap',
         flags: 'flags/flags',
         users: 'users/companyUsers',
         isSME: 'isSME',
         isActiveStudyInSetupStage: 'companies/isActiveStudyInSetupStage',
      }),

      isCompleted() {
         return this.$store.getters[`${this.expenseType}/isCompleted`];
      },

      periodId() {
         return this.$route.params.periodId;
      },

      periodIds() {
         return this.$store.getters[`${this.expenseType}/supportedPeriodIds`];
      },

      companyId() {
         if (this.$route.params.id) {
            return this.$route.params.id;
         } else {
            return this.$companyId;
         }
      },

      statuses() {
         return Object.values(this.mergedStatuses);
      },

      isAssignmentMarkCompletedDisabled() {
         const assigneeId = this.isRndig ? null : this.profile.id;

         return isAssignmentMarkCompletedDisabledRule({
            isCompleted: this.isCompleted,
            hasPendingSaveRequests: this.hasPendingSaveRequests,
            isRndig: this.isRndig,
            statuses: this.statuses,
            selfStatus: this.mergedStatuses[this.profile.id],
            hasCompletedAssignmentsInPeriod: this.hasCompletedAllAssignmentsInPeriod(assigneeId),
            // Employees-only: an employee's assignee can edit the flagged value
            // themselves and re-attempt completion without a separate verify step, so
            // an unresolved flag should block them until they do. Contractors/supplies/
            // cloud vendor data isn't edited the same way by the assignee, so those
            // sections keep their original behavior -- unaffected by this gate.
            hasUnresolvedFlags:
               this.expenseType === 'employees' ? this.hasUnresolvedFlagsForPeriod : false,
            flagsStale: this.flagsStale,
         });
      },

      activeAssignments() {
         const assignmentType = resolveAssignmentType(this.expenseType);
         return this.activeStudy[assignmentType];
      },

      isMarkCompletedDisabled() {
         if (!this.assignmentsLoaded) return true;
         return isSectionMarkCompletedDisabledRule({
            crossPeriodMergedStatuses: this.crossPeriodMergedStatuses,
            isCompleted: this.isCompleted,
            hasUnresolvedFlags: this.hasUnresolvedFlags,
            hasPendingSaveRequests: this.hasPendingSaveRequests,
            allAssignmentsCompleted: this.activeAssignments.every(({completed}) => !!completed),
         });
      },

      hasPendingSaveRequests() {
         return this.$store.getters[`${this.expenseType}/hasPendingSaveRequests`];
      },

      period() {
         return this.studyPeriodMap[this.periodId];
      },

      assignedUsers() {
         return this.$store.getters[`${this.expenseType}/assignedUsersByPeriod`](this.periodId);
      },

      crossPeriodMergedStatuses() {
         return this.$store.getters[`${this.expenseType}/crossPeriodMergedStatuses`];
      },

      mergedStatuses() {
         return this.$store.getters[`${this.expenseType}/mergedStatusesByPeriod`](this.periodId);
      },

      accountCounts() {
         return this.$store.getters[`${this.expenseType}/accountCountsByAssigneeByPeriod`](
            this.periodId
         );
      },

      entityLabelSingularLower() {
         return (
            EXPENSE_ENTITY_LABEL_CONFIG[this.expenseType]?.singularLower ||
            EXPENSE_ENTITY_LABEL_CONFIG.supplies.singularLower
         );
      },

      entityLabelPluralLower() {
         return (
            EXPENSE_ENTITY_LABEL_CONFIG[this.expenseType]?.pluralLower ||
            EXPENSE_ENTITY_LABEL_CONFIG.supplies.pluralLower
         );
      },

      flagCheckSection() {
         return this.$store.getters[`${this.expenseType}/flagCheckSection`];
      },

      // Feeds isMarkCompletedDisabled ("Mark Section Complete"). Checked across every
      // period, not just the one currently selected -- this same gate is also rendered
      // on ExpensesIntro.vue's cross-period overview page, and the two must agree
      // regardless of which page (or which period) the user is on.
      hasUnresolvedFlags() {
         const relevantFlags = getRelevantFlags({
            flags: this.flags,
            studyId: this.activeStudyId,
            checkSection: this.flagCheckSection,
            userId: this.profile.id,
            includeAllUsers: this.isRndig,
         });

         return hasUnresolvedExpenseFlags(relevantFlags, this.$constants().FlagStates);
      },

      // Feeds isAssignmentMarkCompletedDisabled ("Mark Assignment Complete") -- that
      // submit passes a periodId, and the server only re-runs data-check rules for
      // the period being completed, so only THIS period's flags are relevant here.
      hasUnresolvedFlagsForPeriod() {
         const relevantFlags = getRelevantFlags({
            flags: this.flags,
            period: this.period?.period,
            studyId: this.activeStudyId,
            checkSection: this.flagCheckSection,
            userId: this.profile.id,
            includeAllUsers: this.isRndig,
         });

         return hasUnresolvedExpenseFlags(relevantFlags, this.$constants().FlagStates);
      },

      // True once any summary value has been saved since flags were last (re)loaded --
      // lets the button re-enable immediately after an edit instead of staying blocked
      // by a flag the server hasn't had a chance to re-validate yet.
      flagsStale() {
         return this.$store.getters[`${this.expenseType}/flagsStale`];
      },
   },
   methods: {
      async submit(hasPeriod) {
         const config = {
            companyId: this.companyId,
            ...(hasPeriod && {periodId: this.period.period}),
            //assigneeId
         };
         try {
            this.dataValidationLoading = true;
            await this.waitForPendingSaveRequests();
            await this.$store.dispatch(`${this.expenseType}/submit`, config);
         } catch (error) {
            // Completing a period re-runs the section's data-check rules server-side --
            // a MODEL_VALIDATION_ERROR here means new flags were just raised (data changed
            // since the last verification pass), not a transient failure. The flags panel
            // (already refreshed below) is the verification block itself, so no separate
            // error toast is shown for this case -- only for a genuine, non-flag failure.
            const errorCode = error?.response?.data?.errors?.[0]?.code;
            if (errorCode !== ErrorCodes.MODEL_VALIDATION_ERROR) {
               this.$store.commit('showAlert', {
                  fallbackMsg: 'Unable to complete this section. Please try again.',
                  seconds: 5,
               });
            }
         } finally {
            await Promise.all([this.loadAssignments(), this.loadFlag()]);
            this.dataValidationLoading = false;
            this.scrollToTopOfPage();
         }
      },

      async waitForPendingSaveRequests() {
         const getterName = `${this.expenseType}/hasPendingSaveRequests`;
         while (this.$store.getters[getterName]) {
            await new Promise((resolve) => setTimeout(resolve, 50));
         }
      },

      hasCompletedAllAssignmentsInPeriod(assigneeId = null) {
         const {lower, upper} = this.studyPeriodMap[this.periodId].period;
         const matchingAssignments = this.activeAssignments.filter(({period, assigneeId: id}) => {
            const samePeriod = period.lower === lower && period.upper === upper;
            const sameAssignee = !assigneeId || String(id ?? null) === String(assigneeId ?? null);

            return samePeriod && sameAssignee;
         });

         return (
            matchingAssignments.length > 0 &&
            matchingAssignments.every(({completed}) => !!completed)
         );
      },

      userCardTitle(id) {
         if (id === null || id === undefined) {
            return 'Unassigned';
         }

         const selectedUser = Object.values(this.users).find((user) => user.id === id);

         if (!selectedUser) {
            return 'Assigned to Unknown User';
         }

         let title = `Assigned to ${selectedUser?.firstName} ${selectedUser?.lastName}`;

         if (selectedUser?.title && selectedUser?.department) {
            title += ` (${selectedUser.title} - ${selectedUser.department})`;
         } else if (selectedUser?.title || selectedUser?.department) {
            title += ` (${selectedUser.title || selectedUser.department})`;
         }
         return title;
      },

      async loadAssignments() {
         await this.$store.dispatch('companies/loadStudyAssignments', {
            companyId: this.companyId,
         });
         this.assignmentsLoaded = true;
      },

      async loadFlag() {
         if (this.expenseType === 'contractors') return;

         // No period filter -- hasUnresolvedFlags (feeding "Mark Section Complete")
         // needs to see unresolved flags across every period, not just this one.
         // FlagsVerifications.vue's own per-period display filters this client-side.
         await this.$store.dispatch('flags/loadFlags', {
            companyId: this.$companyId,
            checkSection: this.flagCheckSection,
            studyId: this.activeStudyId,
         });
         this.$store.commit(`${this.expenseType}/clearFlagsStale`);
      },

      async loadData() {
         this.loaded = false;

         await this.$store.dispatch('users/loadCompanyUsers', {companyId: this.companyId});
         await this.$store.dispatch('projects/loadProjects', {
            companyId: this.$companyId,
         });
         if (this.expenseType === 'employees') {
            await this.$store.dispatch('activities/loadActivities', {
               companyId: this.$companyId,
            });
         }

         await this.$store.dispatch(`${this.expenseType}/loadAllAssignmentsByCompanyId`, {
            companyId: this.$companyId,
         });
         await this.$store.dispatch(`${this.expenseType}/loadData`, {
            companyId: this.$companyId,
         });
         await Promise.all([this.loadAssignments(), this.loadFlag()]);

         if (
            this.isSME &&
            (!this.periodIds.includes(Number(this.periodId)) || this.assignedUsers.length === 0)
         ) {
            await this.$router.push({name: `${this.expenseType}-table`});
            return;
         }

         this.loaded = true;
      },
   },

   watch: {
      activeStudy() {
         if (this.isActiveStudyInSetupStage && !this.isRndig) {
            this.$router.push({name: 'Home'});
         }
      },

      // Clears dataValidationLoading in the SAME reactive flush as periodId itself
      // changing (both are plain reactive updates Vue batches into one render), so
      // there's never a render with the new periodId but the old (pre-clear) loading
      // state -- see beforeRouteUpdate below for why that ordering matters.
      periodId() {
         this.dataValidationLoading = false;
      },
   },

   // Reload flags for the new period *before* the route (and this.periodId) actually
   // changes, holding dataValidationLoading true for the duration. PerPeriodMessage's
   // cachedMessageKey freezes on the still-current period's message while that's true,
   // so switching periods swaps straight to the new period's correct message instead of
   // flashing a message computed from the new periodId against the old period's flags.
   // dataValidationLoading is deliberately NOT cleared here -- doing so before next()
   // would render the new flags against the still-old periodId, a third, wrong,
   // transient message; the periodId watcher above clears it once the route (and
   // this.periodId) has actually caught up.
   async beforeRouteUpdate(to, from, next) {
      if (to.params.periodId !== from.params.periodId) {
         this.dataValidationLoading = true;
         await this.loadFlag();
      }
      next();
   },

   async created() {
      await this.loadData();
   },
};
</script>

<style lang="scss" scoped>
::v-deep {
   .b-table-sticky-header {
      scroll-padding-top: 95px;
   }

   .b-table {
      font-size: 15px;
   }
}

.checkbox-staff-validation {
   font-size: 12px;
   line-height: 24px;
   border-top: 1px solid rgb(226, 226, 226);
   padding-top: 1rem;
}

.assignment-completion {
   display: flex;
   justify-content: flex-end;
   padding-right: 3px;
   padding-bottom: 3px;
}

.assignment-status,
.assignment-title {
   font-size: 1rem;
   font-weight: 600;
   color: black;
}

.assigned-flags-wrapper {
   background: #fff6000d;
   border-radius: 1.25rem;
   border: 1px solid #ffae004d;
   padding: 1.5rem 1rem;

   &--resolved {
      background: #22b56c08;
      border-color: #22b56c;
   }
}

.flags-title {
   font-size: 0.875rem;
}

.flags-section-header {
   gap: 0.5rem;
   align-items: baseline;
}

.assigned-flags-title-text {
   font-size: 0.75rem;
   font-weight: 600;
   color: black;
   margin: 0;
}

::v-deep .collapse-card {
   box-shadow: none !important;
   padding: 0;
   margin-bottom: 10px;
}

.verify-flag-list-wrapper {
   padding-left: 1.25rem;
}

.verify-flag-list-item {
   display: flex;
   align-items: baseline;
   gap: 0.375rem;
}

.flag-issue {
   font-size: 0.75rem;
   margin: 0;
}

.verify-selected-flags-btn {
   width: 8.75rem;
   margin-top: 1.25rem;
   font-size: 0.625rem;
}

::v-deep .verified-checkbox {
   label::after,
   label::before {
      background-color: #cccccc !important;
   }
}
::v-deep .card-body {
   padding-left: 0;
   padding-right: 0;

   .b-table.table tr td {
      font-size: 0.75rem;
      white-space: nowrap;
   }
}

.account-section {
   box-shadow: none !important;
   padding: 0 !important;

   @include lg {
      margin-top: 1.5rem;
   }

   .account-data {
      display: flex;
      flex-direction: column-reverse;
      align-items: stretch;
      justify-content: space-between;

      @include lg {
         // padding-left: 3rem;
         flex-direction: row;
      }
   }
}

.collapse-wrapper {
   position: relative;
}

.card-spinner-overlay {
   position: absolute;
   top: 0;
   left: 0;
   width: 100%;
   height: 100%;
   background-color: white;
   z-index: 20;
   display: flex;
   justify-content: center;
   border-radius: 23px;
   align-items: center;
   pointer-events: none; /* allow spinner to block but not break layout */
}

.spinner-lg {
   width: 2.5rem;
   height: 2.5rem;
}
.scroll-x {
   overflow-x: auto;
   width: 100%;
   box-sizing: border-box;
}

.table-info-row {
   background-color: #e6f1fb4d;
   border: 1px solid #0778cd33;
   border-radius: 1rem;
   width: 100%;
   margin: 0;
   height: 1.625rem;
   text-align: left;
   padding-left: 0.75rem;
   padding-top: 0.1875rem;
   font-size: 12px;
}

.top-heading-row {
   th {
      z-index: 6 !important;
      top: 0 !important;
   }
}

th {
   top: 2.1rem !important;
}

.assignment-table-wrapper {
   position: relative;

   .data-validation-loading-overlay {
      p {
         position: absolute;
         z-index: 100;
         inset: 0;
         display: flex;
         align-items: center;
         justify-content: center;
         background-color: rgba(255, 255, 255, 0.87);
         backdrop-filter: blur(3px);
         text-align: center;
         font-size: 1rem;
         padding: 0;
         margin: 0;
      }
      .spinner-border {
         position: absolute;
         margin-top: 180px;
      }
   }
}
</style>
